import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { Storage, FindInPage, History, AccountTree } from '@material-ui/icons';

import { hasPermission, createHistory, parseURL, hasAtLeastOnePermission } from '../../sc-utilities';
import { Redirect } from 'react-router-dom';
import WarehouseList from './WarehouseList';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    progressBarr: {
        width: '100%',
        height: 5
    }
});

const Warehouse = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, warehouseID: null, redirectPath: '', search: '' });
    let searchParams = parseURL(window.location.search);

    let visible = columns && columns['Warehouse'];

    const tableActionsRow = [
        {
            icon: 'FindInPage',
            id: `warehouse_details`,
            tooltip: translations['warehouse_details'] || 'Warehouse details',
            onClick: (event, rowData) => gotoWarehouseDetails(event, rowData),
            hidden: !hasPermission(permissions, "warehouse_load")
        },
        {
            icon: 'Colli',
            tooltip: translations["warehouse"] || "Warehouse",
            onClick: (event, rowData) => goToColli(event, rowData),
            hidden: !hasPermission(permissions, "colli_list")
        },
        {
            icon: 'ColliType',
            tooltip: translations["edit_structure"] || "Edit structure",
            onClick: (event, rowData) => goToColliType(event, rowData),
            hidden: !hasPermission(permissions, "colli_type_list")
        },
        {
            icon: 'History',
            tooltip: translations["colli_history"] || "Colli history",
            onClick: (event, rowData) => goToColliHistory(event, rowData),
            hidden: !hasPermission(permissions, "colli_history_list")
        }
    ];

    let warehouseID = null;
    let warehouseInventoryID = null

    const getIcon = icon => {
        switch (icon) {
            case 'FindInPage':
                return <FindInPage id={`warehouse_details_${warehouseID}`} />
            case 'Colli':
                return <Storage id={`goto_colli_${warehouseInventoryID}`} />
            case 'ColliType':
                return <AccountTree id={`goto_colli_type_${warehouseInventoryID}`} />
            case 'History':
                return <History id={`goto_colli_history_${warehouseID}`} />
            default:
                return <> </>
        }
    };

    let tableActionRowLength = tableActionsRow.filter(tableRowAction => !tableRowAction.hidden).length;
    let minMaxWidth = tableActionRowLength > 6 ? 295 : tableActionRowLength * 50;

    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: minMaxWidth, maxWidth: minMaxWidth },
            filtering: false,
            render: rowData => {
                warehouseID = rowData.WarehouseID
                warehouseInventoryID = rowData.WarehouseInventoryID
                return tableActionsRow.map(action => {
                    if (action.hidden) {
                        return null;
                    }
                    return (
                        <Tooltip key={`${action.icon}-${rowData.warehouseID}`} title={action.tooltip}>
                            <IconButton
                                component='div'
                                onClick={(event) => action.onClick(event, rowData)}
                            >
                                {
                                    getIcon(action.icon)
                                }
                            </IconButton>
                        </Tooltip>
                    )
                })
            }
        },
        {
            title: translations["id"] || "ID", field: 'WarehouseInventoryID',
            hidden: visible ? visible['WarehouseInventoryID'] : false,
            defaultFilter: searchParams && searchParams.WarehouseInventoryID ? searchParams.WarehouseInventoryID : '',
            width: 100,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.WarehouseInventoryID ? rowData.WarehouseInventoryID : translations['short_not_assigned'] || 'n/a'}</Typography>;
            },
        },
        {
            title: translations["name"] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["location"] ? translations["location"].charAt(0).toUpperCase() + translations["location"].substring(1, translations["location"].length) : 'Location', field: 'Location',
            hidden: visible ? visible['Location'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Location ? rowData.Location : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["contact_person"] || 'Contact person', field: 'ContactPerson',
            hidden: visible ? visible['ContactPerson'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ContactPerson ? rowData.ContactPerson : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["deleted"] || 'Deleted', field: 'Deleted',
            hidden: visible ? visible['Deleted'] : false,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0'],
            filtering: true,
            width: 100,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        }
    ];

    const gotoWarehouseDetails = (event, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, warehouseID: rowData.WarehouseID, redirectPath: `/warehouses/edit`, search: `warehouseID=${rowData.WarehouseID}` })
    }

    const goToColli = (event, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, warehouseID: rowData.WarehouseID, redirectPath: `/logistic/container`, search: `WarehouseInventoryID=${rowData.WarehouseInventoryID}` })
    }

    const goToColliType = (event, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, warehouseID: rowData.WarehouseID, redirectPath: `/logistic/colli-type`, search: `WarehouseInventoryID=${rowData.WarehouseInventoryID}` })
    }

    const goToColliHistory = (event, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, warehouseID: rowData.WarehouseID, redirectPath: `/logistic/colli-history`, search: `WarehouseInventoryID=${rowData.WarehouseInventoryID}` })
    }

    if (redirectOptions.redirect) {
        return <Redirect to={
            {
                pathname: redirectOptions.redirectPath,
                search: redirectOptions.search,
                state: { WarehouseID: redirectOptions.warehouseID }
            }
        } />;
    }

    if (!hasAtLeastOnePermission(permissions, [
        "warehouse_load",
        "colli_list",
        'colli_type_list',
        "colli_history_list"
    ])) {
        tableColumns.shift();
    }

    return (
        <div className={classes.root}>
            {
                hasPermission(permissions, 'warehouse_list') ?
                    <WarehouseList
                        tableColumns={tableColumns}
                        tableRef={tableRef}
                    /> : null
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(Warehouse));


/**
 * Translations:
 *   Warehouse
 *   edit_warehouse
 *   contact_person
 */